import React, { memo, useCallback } from 'react'

import { classnames } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { shape, string, bool, func, number } from 'prop-types'

function ImageSlide({ media, title, isFirstImage, loadLowQualityImage = false, onLoad, onError, slideIndex }) {
  const { model, imageName, alt, width, height } = media
  const { imageName: modelImageName } = model ?? {}
  const { url: fullscreenVariantUrl, large: largeVariant } = modelImageName ?? {}
  const { url: largeVariantUrl } = largeVariant ?? {}

  const fullScreenImageUrl = fullscreenVariantUrl || imageName
  const largeImageUrl = largeVariantUrl || imageName
  const imageUrl = (() => {
    if (loadLowQualityImage) {
      return largeImageUrl || imageName
    }

    return fullScreenImageUrl || largeVariantUrl
  })()

  const handleLoad = useCallback(() => {
    if (onLoad) {
      onLoad(slideIndex)
    }
  }, [onLoad, slideIndex])

  const handleError = useCallback(() => {
    if (onError) {
      onError(slideIndex)
    }
  }, [onError, slideIndex])

  // The slide is laid out as `height: 100%; width: auto`, so the browser derives the reserved
  // width from these attributes. A guessed pair reserves the wrong box and shifts on decode, so
  // emit them only when both intrinsic dimensions are known.
  const hasIntrinsicSize = width > 0 && height > 0
  const caption = alt || title
  // The ratio lets CSS size the box to the picture itself (no letterbox), so the caption can
  // anchor to the image's real bottom edge while the image still fills the carousel height.
  const imageStyle = hasIntrinsicSize ? { '--gallery-image-ratio': width / height } : undefined

  return (
    <figure className="gallery-area-top-carousel__figure">
      <img
        alt={caption}
        className={classnames(
          'gallery-area-top-carousel__image',
          hasIntrinsicSize && 'gallery-area-top-carousel__image--with-ratio'
        )}
        style={imageStyle}
        src={imageUrl}
        loading={isFirstImage ? 'eager' : 'lazy'}
        fetchpriority={isFirstImage ? 'high' : 'auto'} // eslint-disable-line react/no-unknown-property
        decoding={isFirstImage ? 'sync' : 'async'}
        width={hasIntrinsicSize ? width : undefined}
        height={hasIntrinsicSize ? height : undefined}
        onLoad={handleLoad}
        onError={handleError}
      />
      {/* Same text as the alt: hidden from AT so screen readers do not announce each slide twice */}
      <figcaption className="gallery-area-top-carousel__caption cgt-caption" aria-hidden="true">
        {caption}
      </figcaption>
    </figure>
  )
}

ImageSlide.propTypes = {
  media: shape({
    model: shape({}),
    imageName: string,
    alt: string,
    width: number,
    height: number,
  }).isRequired,
  title: string.isRequired,
  isFirstImage: bool.isRequired,
  loadLowQualityImage: bool,
  onLoad: func,
  onError: func,
  slideIndex: number,
}

export default memo(ImageSlide)
